import {
  ILoginAccessToken,
  IShopAccess,
} from '../api/model/login-access-token';
import { dateFromServer } from '../utility/dates';

export type AuthProviderType = 'google' | 'facebook' | 'internal';

export interface IUserProfile {
  id: string;
  displayName: string;
  emailAddress?: string;
  authProvider: AuthProviderType;
  profileImageBase64?: string;
  accessToken?: ILoginAccessToken;
}

// Utility type for creating user profiles from different auth providers
export type UserProfileFromProvider<T> = Omit<IUserProfile, 'authProvider'> & {
  authProvider: T;
};

// Type guards for auth provider checking
export function isFacebookProfile(
  profile: IUserProfile
): profile is UserProfileFromProvider<'facebook'> {
  return profile.authProvider === 'facebook';
}

export function isGoogleProfile(
  profile: IUserProfile
): profile is UserProfileFromProvider<'google'> {
  return profile.authProvider === 'google';
}

export class UserProfile implements IUserProfile {
  needsRefresh() {
    if (!this.accessToken || !this.accessToken.expiresUtc) {
      return false;
    }
    const expirationDate = dateFromServer(this.accessToken.expiresUtc);
    const currentDate = new Date();
    const fourHoursFromNow = new Date(
      currentDate.getTime() + 4 * 60 * 60 * 1000
    );

    return expirationDate <= fourHoursFromNow;
  }

  expiresAtMilliseconds() {
    if (!this.accessToken || !this.accessToken.expiresUtc) {
      return 0;
    }
    const expirationDate = dateFromServer(this.accessToken.expiresUtc);
    const currentDate = new Date();
    return expirationDate.getTime() - currentDate.getTime();
  }

  private readonly _profile: IUserProfile;

  get profile(): IUserProfile {
    return this._profile;
  }

  constructor(profile: IUserProfile) {
    this._profile = profile;
  }

  get id(): string {
    return this._profile.id;
  }

  get displayName(): string {
    return this._profile.displayName;
  }

  get emailAddress(): string | undefined {
    return this._profile.emailAddress;
  }

  get authProvider(): AuthProviderType {
    return this._profile.authProvider;
  }

  get profileImageBase64(): string | undefined {
    return this._profile.profileImageBase64;
  }

  get accessToken(): ILoginAccessToken | undefined {
    return this._profile.accessToken;
  }

  get isLoggedIn(): boolean {
    return (
      this.accessToken !== undefined &&
      new Date(this.accessToken.expiresUtc) > new Date()
    );
  }

  get shopAccess(): IShopAccess[] {
    return this.accessToken?.shopAccess || [];
  }

  hasShopAccess(shopName: string): boolean {
    return this.shopAccess.some(
      (shop) =>
        shop.shopName.toLowerCase() === shopName.toLowerCase() && shop.active
    );
  }

  getShopRoles(shopName: string): string[] {
    return (
      this.shopAccess.find(
        (shop) => shop.shopName.toLowerCase() === shopName.toLowerCase()
      )?.roles || []
    );
  }

  hasRole(shopName: string, role: string): boolean {
    const roles = this.getShopRoles(shopName);
    return roles.includes(role);
  }

  getShopClaims(shopName: string): { [key: string]: string } {
    return (
      this.shopAccess.find(
        (shop) => shop.shopName.toLowerCase() === shopName.toLowerCase()
      )?.claims || {}
    );
  }

  getClaim(shopName: string, claim: string): string | undefined {
    return this.getShopClaims(shopName)[claim];
  }

  getActiveShops(): IShopAccess[] {
    return this.shopAccess.filter((shop) => shop.active);
  }

  get isCustomer(): boolean {
    return this.accessToken?.isCustomer ?? false;
  }

  get hasActiveShops(): boolean {
    return this.getActiveShops().length > 0;
  }

  hasValidAccessToken(): boolean {
    return (
      !!this.accessToken &&
      !!this.accessToken.encodedLoginAccessToken &&
      new Date(this.accessToken.expiresUtc) > new Date()
    );
  }

  toJSON(): IUserProfile {
    return this._profile;
  }

  static fromJSON(json: IUserProfile): UserProfile {
    return new UserProfile(json);
  }

  async performLogoutOperations() {}
}
